// The two filter popovers — the searchable value picker and the dual-handle range picker —
// shared by BOTH filter bars: <FilterChipBar> (StatsFilterKit, player bio filters) and
// <EventFilterBar> (EventMapKit, shot/pass filters).
//
// The legacy keeps two copies (renderers-stats.js openValuePicker/openRangePicker and
// renderers-shotmap.js openEventValuePicker/openEventRangePicker) with the SAME DOM and
// CSS classes; only four things differ, so they're props here, not a second copy (04l
// fork 2): the search label, whether options get an exclude (✕) button (the Colour-by
// chip is a pick-one mode, so it has none), how the readout + domain labels format, and
// the value rounding (Stats' ranges are integers; event ranges have a precision). The two
// filter ENGINES stay separate, as the legacy keeps them.

import { LIVE } from "../live/flag.js";
import { fuzzyFilter, withCountryAliases } from "../data/fuzzy-search.js";
import { Fragment, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** A value's heading + icon in a grouped value picker (the League chip: country + flag). */
export interface OptionInfo {
  group: string;
  icon?: ReactNode;
  /** Groups sort by this first (lower first; default 1, "International" 2): the League chip's
   *  "Top leagues" is 0. Within a group, `rank` (lower first) before the name. */
  groupRank?: number;
  rank?: number;
  /** The words shown in the list, when they differ from the value (a league without its
   *  "(England)": the flag and heading say it — Ben, 2026-10-18). */
  text?: string;
}

export interface PopoverPos {
  left: number;
  top: number;
}

/** Popover position under a chip (page coords, like the legacy's rect + scroll offsets). */
export function popoverPosUnder(el: HTMLElement): PopoverPos {
  const rect = el.getBoundingClientRect();
  return { left: rect.left + window.scrollX, top: rect.bottom + window.scrollY + 4 };
}

export function ExcludeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/** Close on a press outside the popover — except on a `.chip-value` (that chip's own click
 *  toggles it, so closing here first would immediately reopen it). */
function useOutsideClose(ref: React.RefObject<HTMLDivElement | null>, onClose: () => void) {
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!ref.current?.contains(t) && !t.classList.contains("chip-value")) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [ref, onClose]);
}

// --- the searchable value picker (categorical) --------------------------------
export function ValuePicker({
  label,
  pos,
  options,
  activeValue,
  activeExcluded,
  excludable = true,
  selected,
  optionInfo,
  onPick,
  onCommit,
  onClose,
}: {
  /** The filter's label — the search placeholder reads "Search {label}…". */
  label: string;
  pos: PopoverPos;
  options: string[];
  /** The current value (unwrapped from an exclude), if any. */
  activeValue: string | undefined;
  activeExcluded: boolean;
  /** false for a pick-one mode (Colour-by) — no exclude button. */
  excludable?: boolean;
  /** Tick-list mode (live, Ben 2026-10-16): the values currently ticked. A click on a value
   *  ticks / unticks it (onPick) and the list stays open; the caller decides the new set. */
  selected?: readonly string[];
  /** Per value: a group heading (e.g. the league's country) and an icon (its flag). Given → the
   *  list is grouped under headings, and the search also matches the heading (Ben, 2026-10-16). */
  optionInfo?: (value: string) => OptionInfo | undefined;
  onPick: (value: string, exclude: boolean) => void;
  /** Tick-list mode: the ticked values, applied once when the list closes (outside click, Esc,
   *  "Done", another chip opened). */
  onCommit?: (values: string[]) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  // Tick-list (Ben, 2026-10-17): ticks are only recorded while the list is open — instantly, with
  // no data loading between clicks, so 4 or 5 nationalities can be ticked in a row — and applied
  // once, when it closes (this component unmounts).
  const [draft, setDraft] = useState<readonly string[] | null>(null);
  // The values ticked when the list opened (fixed while it's open) — listed first.
  const [pinned] = useState<ReadonlySet<string>>(() => new Set(selected && onCommit ? selected : []));
  const shownSelected = draft ?? selected;
  const commit = useRef({ draft, selected, onCommit });
  commit.current = { draft, selected, onCommit };
  useEffect(
    () => () => {
      const { draft: d, selected: s, onCommit: apply } = commit.current;
      if (d && apply && d.join("\u0000") !== (s ?? []).join("\u0000")) apply([...d]);
      // Opened by "+ Add filter" and closed with nothing ticked: the chip goes (an empty commit).
      else if (!d && apply && s && s.length === 0) apply([]);
    },
    [],
  );
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useOutsideClose(ref, onClose);

  const q = query.trim().toLowerCase();
  const groupOf = (o: string) => optionInfo?.(o)?.group ?? "";
  // Live: forgiving ("Premiere Ligue" finds Première Ligue; a country finds its leagues); the
  // demo keeps its plain match.
  const opts = LIVE
    ? q
      ? // Other names for a country count too ("Holland" → the Dutch leagues / nationality).
        fuzzyFilter(options, query, (o) => withCountryAliases(`${o} ${groupOf(o)}`))
      : [...options]
    : options.filter((o) => !q || o.toLowerCase().includes(q) || groupOf(o).toLowerCase().includes(q));
  if (optionInfo) {
    // Grouped: pinned groups first (groupRank), then headings A→Z ("International" last); within a
    // group by `rank` (e.g. Ben's top-league order), then by name.
    const info = (o: string) => optionInfo(o);
    const gRank = (o: string) => info(o)?.groupRank ?? (groupOf(o) === "International" ? 2 : 1);
    const rank = (o: string) => info(o)?.rank ?? Number.MAX_SAFE_INTEGER;
    opts.sort(
      (a, b) => gRank(a) - gRank(b) || groupOf(a).localeCompare(groupOf(b)) || rank(a) - rank(b) || a.localeCompare(b),
    );
  }
  // Tick-list: what was ticked when the list opened comes first (Ben, 2026-10-17: six countries
  // spread through an A→Z list were a nightmare to find), then a thin line, then the rest — no
  // "Selected" / "All" headings (too tall). Fixed while it's open, so nothing jumps as you click.
  const pinnedFirst = pinned.size > 0;
  if (pinnedFirst) {
    const top = opts.filter((o) => pinned.has(o));
    const rest = opts.filter((o) => !pinned.has(o));
    opts.splice(0, opts.length, ...top, ...rest);
  }
  // League lists keep their headings (Top leagues, countries) below the line; ticked ones have none.
  const headOf = (o: string) => (pinned.has(o) || !optionInfo ? null : groupOf(o));

  return createPortal(
    <div className="value-picker" ref={ref} style={{ left: pos.left, top: pos.top }}>
      <input
        className="value-picker-search"
        placeholder={`Search ${label.toLowerCase()}…`}
        autoComplete="off"
        spellCheck={false}
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="value-picker-list">
        {opts.length ? (
          opts.map((o, i) => {
            const head = headOf(o);
            const heading = head !== null && (i === 0 || headOf(opts[i - 1]!) !== head) ? head : null;
            const divider = pinnedFirst && i > 0 && pinned.has(opts[i - 1]!) && !pinned.has(o);
            const ticked = shownSelected?.includes(o) ?? false;
            const isActive = selected ? ticked || (activeExcluded && o === activeValue) : o === activeValue;
            const cls = `value-picker-option${isActive ? (activeExcluded ? " excluded" : " selected") : ""}`;
            return (
              <Fragment key={o}>
                {divider && <div className="value-picker-divider" />}
                {heading !== null && <div className="value-picker-group">{heading}</div>}
                <div className={cls}>
                <span
                  className="value-picker-option-label"
                  onClick={() => {
                    if (selected && onCommit) {
                      const now = shownSelected ?? [];
                      setDraft(now.includes(o) ? now.filter((v) => v !== o) : [...now, o]);
                      return;
                    }
                    onPick(o, false);
                  }}
                >
                  {selected && (
                    <span className={`value-picker-tick${ticked ? " on" : ""}`} aria-hidden="true">
                      {ticked ? "✓" : ""}
                    </span>
                  )}
                  {optionInfo?.(o)?.icon}
                  {optionInfo?.(o)?.text ?? o}
                </span>
                {excludable && (
                  <button
                    type="button"
                    className={`value-picker-exclude-btn${isActive && activeExcluded ? " active" : ""}`}
                    title={`Exclude ${o}`}
                    aria-label={`Exclude ${o}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      commit.current.draft = null; // the exclude replaces any ticks
                      onPick(o, true);
                    }}
                  >
                    <ExcludeIcon />
                  </button>
                )}
                </div>
              </Fragment>
            );
          })
        ) : (
          <div className="value-picker-empty">No matches</div>
        )}
      </div>
      {selected && onCommit && (
        <button type="button" className="value-picker-done" onClick={onClose}>
          Done
        </button>
      )}
    </div>,
    document.body,
  );
}

// --- the dual-handle range picker ---------------------------------------------
export function RangePicker({
  pos,
  range,
  valueToPct,
  pxToValue,
  formatReadout,
  labels,
  zeroTick = false,
  onChange,
  onClose,
}: {
  pos: PopoverPos;
  /** The module's own mutable range entry — dragged IN PLACE (the consumers' contract). */
  range: { min: number; max: number };
  valueToPct: (v: number) => number;
  /** Pointer offset → value, clamped + rounded (integers for Stats, a precision for events). */
  pxToValue: (px: number, trackWidth: number) => number;
  formatReadout: (range: { min: number; max: number }) => string;
  /** The domain-end labels under the track (2 spans, or 3 with a relative zero label). */
  labels: ReactNode;
  /** Stats' relative ranges mark the reference player's own value. */
  zeroTick?: boolean;
  onChange: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const minRef = useRef<HTMLDivElement>(null);
  const maxRef = useRef<HTMLDivElement>(null);
  const [, redraw] = useReducer((x: number) => x + 1, 0);
  // The latest mapping without re-wiring the drag listeners on every render.
  const pxToValueRef = useRef(pxToValue);
  pxToValueRef.current = pxToValue;

  useOutsideClose(ref, onClose);

  // Native drag listeners on the handles (non-passive touch, no React passive-touch
  // caveats) — mutate the range in place, redraw locally, throttle the consumer's
  // recompute to one per animation frame (both legacy pickers do the same).
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let rafPending = false;
    const scheduleOnChange = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        onChange();
      });
    };
    const makeStart = (handle: "min" | "max") => (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      const onMove = (ev: MouseEvent | TouchEvent) => {
        const clientX = "touches" in ev ? ev.touches[0]!.clientX : ev.clientX;
        const r = track.getBoundingClientRect();
        const val = pxToValueRef.current(clientX - r.left, r.width);
        if (handle === "min") range.min = Math.min(val, range.max);
        else range.max = Math.max(val, range.min);
        redraw();
        scheduleOnChange();
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onUp);
        onChange();
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onUp);
    };
    const minEl = minRef.current!;
    const maxEl = maxRef.current!;
    const startMin = makeStart("min");
    const startMax = makeStart("max");
    minEl.addEventListener("mousedown", startMin);
    maxEl.addEventListener("mousedown", startMax);
    minEl.addEventListener("touchstart", startMin, { passive: false });
    maxEl.addEventListener("touchstart", startMax, { passive: false });
    return () => {
      minEl.removeEventListener("mousedown", startMin);
      maxEl.removeEventListener("mousedown", startMax);
      minEl.removeEventListener("touchstart", startMin);
      maxEl.removeEventListener("touchstart", startMax);
    };
    // range is a stable object for this open picker; the handlers read it live.
  }, [range, onChange]);

  const lo = valueToPct(range.min);
  const hi = valueToPct(range.max);

  return createPortal(
    <div className="filter-range-picker" ref={ref} style={{ left: pos.left, top: pos.top }}>
      <div className="filter-range-readout">{formatReadout(range)}</div>
      <div className="filter-range-track-wrap" ref={trackRef}>
        <div className="filter-range-track" />
        <div className="filter-range-fill" style={{ left: `${lo}%`, width: `${Math.max(hi - lo, 0)}%` }} />
        {zeroTick && <div className="filter-range-zero-tick" />}
        <div className="filter-range-handle" data-handle="min" tabIndex={0} ref={minRef} style={{ left: `${lo}%` }} />
        <div className="filter-range-handle" data-handle="max" tabIndex={0} ref={maxRef} style={{ left: `${hi}%` }} />
      </div>
      <div className="filter-range-labels">{labels}</div>
    </div>,
    document.body,
  );
}
